import {
  ApiResponse,
  MockResponse,
  FornitoreBase,
  FornitoreDettagliato,
  LookupData,
  MenuItem,
  GruppoFornitore,
  GruppoFornitoreInput,
} from "./types";
import {
  FORNITORI_BASE,
  FORNITORI_DETTAGLIATI,
  LOOKUP_DATA,
  MENU_ITEMS,
  GRUPPI_FORNITORI,
} from "./fornitori-data";
import { PersistenceManager } from "./persistence";

/**
 * Utility per creare risposte mock standardizzate
 */
const createMockResponse = <T>(
  data: T,
  status = 200
): MockResponse<ApiResponse<T>> => ({
  status,
  data: {
    success: status < 400,
    message: status >= 400 ? "Errore nella richiesta" : undefined,
    results: data,
  },
});

/**
 * Utility per creare risposte di lista con paginazione
 */
const createListResponse = <T>(
  items: T[],
  page = 1,
  pageSize = 10
): MockResponse<ApiResponse<T[]>> => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedItems = items.slice(startIndex, endIndex);

  return {
    status: 200,
    data: {
      success: true,
      results: paginatedItems,
      totalCount: items.length,
      currentPage: page,
      totalPages: Math.ceil(items.length / pageSize),
      pageSize,
    },
  };
};

/**
 * Utility per trovare un fornitore per ID
 */
const findFornitoreById = (id: number): FornitoreDettagliato | undefined => {
  return PersistenceManager.getFornitoreDetagliatoById(id) || undefined;
};

/**
 * Simulatore di ricerca avanzata sui fornitori
 */
const searchFornitori = (
  searchTerm?: string,
  stato?: string,
  provincia?: string,
  tipologia?: string
): FornitoreBase[] => {
  let filtered = PersistenceManager.getAllFornitori();

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (f) =>
        f.ragioneSociale.toLowerCase().includes(term) ||
        f.partitaIva.includes(term) ||
        f.email.toLowerCase().includes(term)
    );
  }

  if (stato) {
    filtered = filtered.filter((f) => f.stato === stato);
  }

  if (provincia) {
    filtered = filtered.filter((f) => f.provincia === provincia);
  }

  if (tipologia) {
    filtered = filtered.filter((f) => f.tipologia === tipologia);
  }

  return filtered;
};

/**
 * Helper function per completare i dati del fornitore con valori di default
 */
const createFornitoreCompleto = (payload: any): FornitoreDettagliato => {
  const today = new Date().toISOString().split("T")[0];

  // Valori di default per la struttura complessa FornitoreDettagliato
  const defaultFornitore: FornitoreDettagliato = {
    id: 0, // Verrà sovrascritto dal PersistenceManager
    operatore_economico: {
      stato: payload.operatore_economico?.stato || payload.stato || "attivo",
      possiede_partita_iva:
        payload.operatore_economico?.possiede_partita_iva ?? true,
      partita_iva:
        payload.operatore_economico?.partita_iva ||
        payload.partitaIva ||
        payload.azienda?.partita_iva ||
        "",
      codice_fiscale_ditta:
        payload.operatore_economico?.codice_fiscale_ditta ||
        payload.codiceFiscale ||
        "",
      id_soggetto: payload.operatore_economico?.id_soggetto || null,
    },
    azienda: {
      denominazione:
        payload.azienda?.denominazione ||
        payload.ragioneSociale ||
        payload.nome ||
        "",
      ragione_sociale:
        payload.azienda?.ragione_sociale ||
        payload.ragioneSociale ||
        payload.nome ||
        "",
      forma_giuridica: payload.azienda?.forma_giuridica || "",
      indirizzo: payload.azienda?.indirizzo || payload.indirizzo || "",
      comune: payload.azienda?.comune || payload.citta || "",
      provincia: payload.azienda?.provincia || payload.provincia || "",
      regione: payload.azienda?.regione || null,
      cap: payload.azienda?.cap || payload.cap || "",
      sedi_secondarie: payload.azienda?.sedi_secondarie || null,
      email_pec: payload.azienda?.email_pec || payload.pec || "",
      telefono: payload.azienda?.telefono || payload.telefono || null,
      fax: payload.azienda?.fax || payload.fax || null,
      stato_attivita: payload.azienda?.stato_attivita || null,
      codice_ateco: payload.azienda?.codice_ateco || null,
      codice_ateco_2025: payload.azienda?.codice_ateco_2025 || null,
      ateco_testo: payload.azienda?.ateco_testo || null,
      ateco_2025_testo: payload.azienda?.ateco_2025_testo || null,
      anno_iscrizione_cciaa: payload.azienda?.anno_iscrizione_cciaa || null,
      numero_rea: payload.azienda?.numero_rea || null,
      cciaa_di: payload.azienda?.cciaa_di || null,
      codice_eori: payload.azienda?.codice_eori || null,
      etichette: payload.azienda?.etichette || [],
    },
    referente_legale: {
      nome: payload.referente_legale?.nome || payload.nome || "",
      cognome: payload.referente_legale?.cognome || payload.cognome || "",
      telefono_1:
        payload.referente_legale?.telefono_1 || payload.telefono || null,
      telefono_2:
        payload.referente_legale?.telefono_2 || payload.cellulare || null,
      email: payload.referente_legale?.email || payload.email || null,
      qualifica: payload.referente_legale?.qualifica || "Rappresentante Legale",
      codice_fiscale:
        payload.referente_legale?.codice_fiscale || payload.codiceFiscale || "",
    },
    rating_rischio: payload.rating_rischio || {
      cerved_group_score: null,
      score_sintetico: null,
      score_tempi_pagamento: null,
      consistenza_immobiliare: null,
      situazione_economico_finanziaria: null,
      profilo_strutturale: null,
      rischio_connesso_num_consultazioni: null,
      eventi_negativi: null,
      cgs_valore: null,
      cgr_classe: null,
      cgr_descrizione: null,
      rischio_interno: null,
      variazione_rischio_interno: null,
    },
    dati_economici: payload.dati_economici || {
      fatturato_000_eur: null,
      mol_000_eur: null,
      margine_gestione_finanziaria_000_eur: null,
      ebitda_000_eur: null,
      utile_perdita_000_eur: null,
      attivo_000_eur: null,
      passivo_000_eur: null,
      dipendenti: null,
      anno_riferimento_dati_economici: null,
      fatturato_12m: null,
      media_fatturato: null,
      fatturato_mese: null,
      incassato: null,
      classe_di_fatturato: null,
      mese_riferimento_dati_caricati: null,
      anno_riferimento_dati_caricati: null,
    },
    monitoraggio: payload.monitoraggio || {
      prodotto: null,
      scadenza_monitoraggio: null,
      utenza: null,
    },
    credito_commerciale: payload.credito_commerciale || {
      fido: null,
      fido_commerciale: null,
      fido_dinamico: null,
      fido_dinamico_medio: null,
      variazione_fido_commerciale_vs_dinamico_medio: null,
      azione_da_fido_dinamico: null,
      fido_2: null,
      esposizione: null,
      scaduto: null,
      esposizione_su_fido_commerciale: null,
      dso: null,
      dso_medio: null,
      bp_dso: null,
      bp_dso_medio: null,
      add: null,
      add_medio: null,
      limite_dso: null,
      condizione_di_pagamento: null,
      giorni_nominali: null,
      codice_cliente_interno: null,
    },
    categorie_internal_use: payload.categorie_internal_use || {
      cat1: null,
      cat2: null,
      cat3: null,
      cat4: null,
      cat5: null,
      cat6: null,
      cat7: null,
      cat8: null,
    },
    pagamenti: {
      iban: payload.pagamenti?.iban || payload.iban || null,
      sdi: payload.pagamenti?.sdi || payload.sdi || null,
      metodo_pagamento_preferito:
        payload.pagamenti?.metodo_pagamento_preferito || null,
      note: payload.pagamenti?.note || payload.note || null,
    },
    metadati: {
      fonte: payload.metadati?.fonte || "mock",
      versione_schema: payload.metadati?.versione_schema || "1.0",
      data_creazione: payload.metadati?.data_creazione || today,
      data_ultima_modifica: today,
    },
  };

  // Se il payload ha già la struttura completa, usa quella, altrimenti usa i default
  if (
    payload.operatore_economico &&
    payload.azienda &&
    payload.referente_legale
  ) {
    return { ...defaultFornitore, ...payload };
  }

  return defaultFornitore;
};

/**
 * Mock responses per le API dei fornitori
 */
export const FORNITORI_MOCK_RESPONSES = {
  // Lista fornitori con supporto per filtri e paginazione
  LIST: (params?: {
    search?: string;
    stato?: string;
    provincia?: string;
    tipologia?: string;
    page?: number;
    pageSize?: number;
  }): MockResponse<ApiResponse<FornitoreBase[]>> => {
    const {
      search,
      stato,
      provincia,
      tipologia,
      page = 1,
      pageSize = 10,
    } = params || {};

    const filtered = searchFornitori(search, stato, provincia, tipologia);
    return createListResponse(filtered, page, pageSize);
  },

  // Dettaglio fornitore
  VIEW: (id: number): MockResponse<ApiResponse<FornitoreDettagliato>> => {
    const fornitore = findFornitoreById(id);

    if (!fornitore) {
      return {
        status: 404,
        data: {
          success: false,
          message: "Fornitore non trovato",
          results: {} as FornitoreDettagliato,
        },
      };
    }

    return createMockResponse(fornitore);
  },

  // Aggiunta fornitore
  ADD: (
    payload: any
  ): MockResponse<ApiResponse<{ id: number; message: string }>> => {
    console.log("[MOCK ADD] Payload ricevuto:", payload);

    // Simulazione validazione - controlla se ha almeno i dati minimi
    const ragioneSociale =
      payload.azienda?.ragione_sociale ||
      payload.ragioneSociale ||
      payload.nome;
    const partitaIva =
      payload.operatore_economico?.partita_iva || payload.partitaIva;

    console.log("[MOCK ADD] Validazione:", { ragioneSociale, partitaIva });

    if (!ragioneSociale && !partitaIva) {
      return {
        status: 400,
        data: {
          success: false,
          message:
            "Dati obbligatori mancanti: specificare almeno ragione sociale o partita IVA",
          results: { id: 0, message: "" },
        },
      };
    }

    // Crea un fornitore dettagliato dal payload completando i dati mancanti
    const fornitoreCompleto = createFornitoreCompleto(payload);

    console.log(
      "[MOCK ADD] Fornitore completo da aggiungere:",
      fornitoreCompleto
    );

    // Aggiungi il fornitore al sistema di persistenza
    const result = PersistenceManager.addFornitore(fornitoreCompleto);

    if (!result.success) {
      return {
        status: 500,
        data: {
          success: false,
          message: "Errore durante la creazione del fornitore",
          results: { id: 0, message: "" },
        },
      };
    }

    return createMockResponse(
      {
        id: result.id,
        message: "Fornitore creato con successo",
      },
      200
    );
  },

  // Modifica fornitore
  EDIT: (
    id: number,
    payload: Partial<FornitoreDettagliato>
  ): MockResponse<ApiResponse<{ message: string }>> => {
    const fornitore = findFornitoreById(id);

    if (!fornitore) {
      return {
        status: 404,
        data: {
          success: false,
          message: "Fornitore non trovato",
          results: { message: "" },
        },
      };
    }

    // Aggiorna il fornitore nel sistema di persistenza
    const updatedFornitore = { ...fornitore, ...payload };
    PersistenceManager.updateFornitore(id, updatedFornitore);

    return createMockResponse({
      message: "Fornitore aggiornato con successo",
    });
  },

  // Eliminazione fornitore
  DELETE: (id: number): MockResponse<ApiResponse<{ message: string }>> => {
    const fornitore = findFornitoreById(id);

    if (!fornitore) {
      return {
        status: 404,
        data: {
          success: false,
          message: "Fornitore non trovato",
          results: { message: "" },
        },
      };
    }

    // Elimina il fornitore dal sistema di persistenza
    const deleted = PersistenceManager.deleteFornitore(id);

    if (!deleted) {
      return {
        status: 500,
        data: {
          success: false,
          message: "Errore durante l'eliminazione del fornitore",
          results: { message: "" },
        },
      };
    }

    return createMockResponse({
      message: "Fornitore eliminato con successo",
    });
  },
};

/**
 * Mock responses per i gruppi fornitori
 */
const GRUPPI_FORNITORI_MOCK_RESPONSES = {
  LIST: (params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): MockResponse<ApiResponse<GruppoFornitore[]>> => {
    let gruppi = [...GRUPPI_FORNITORI];

    // Filtro per ricerca
    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      gruppi = gruppi.filter(
        (g) =>
          g.nome.toLowerCase().includes(searchTerm) ||
          g.descrizione.toLowerCase().includes(searchTerm)
      );
    }

    // Paginazione
    const page = params?.page || 1;
    const pageSize = params?.pageSize || 10;
    
    return createListResponse(gruppi, page, pageSize);
  },

  VIEW: (id: number): MockResponse<ApiResponse<GruppoFornitore>> => {
    const gruppo = GRUPPI_FORNITORI.find((g) => g.id === id);
    
    if (!gruppo) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${id} non trovato`,
          results: {} as GruppoFornitore,
        },
      };
    }

    return createMockResponse(gruppo);
  },

  ADD: (payload: GruppoFornitoreInput): MockResponse<ApiResponse<GruppoFornitore>> => {
    // Validazione
    if (!payload.nome || payload.nome.trim().length === 0) {
      return {
        status: 400,
        data: {
          success: false,
          message: "Il nome del gruppo è obbligatorio",
          results: {} as GruppoFornitore,
        },
      };
    }

    // Crea nuovo gruppo
    const newId = Math.max(...GRUPPI_FORNITORI.map((g) => g.id), 0) + 1;
    const today = new Date().toISOString().split("T")[0];
    
    const nuovoGruppo: GruppoFornitore = {
      id: newId,
      nome: payload.nome,
      descrizione: payload.descrizione || "",
      numero_fornitori: 0,
      fornitori_ids: [],
      data_creazione: today,
      data_modifica: today,
    };

    GRUPPI_FORNITORI.push(nuovoGruppo);

    return {
      status: 201,
      data: {
        success: true,
        message: "Gruppo creato con successo",
        results: nuovoGruppo,
      },
    };
  },

  EDIT: (
    id: number,
    payload: GruppoFornitoreInput
  ): MockResponse<ApiResponse<GruppoFornitore>> => {
    const gruppoIndex = GRUPPI_FORNITORI.findIndex((g) => g.id === id);

    if (gruppoIndex === -1) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${id} non trovato`,
          results: {} as GruppoFornitore,
        },
      };
    }

    // Validazione
    if (!payload.nome || payload.nome.trim().length === 0) {
      return {
        status: 400,
        data: {
          success: false,
          message: "Il nome del gruppo è obbligatorio",
          results: {} as GruppoFornitore,
        },
      };
    }

    // Aggiorna gruppo
    const today = new Date().toISOString().split("T")[0];
    GRUPPI_FORNITORI[gruppoIndex] = {
      ...GRUPPI_FORNITORI[gruppoIndex],
      nome: payload.nome,
      descrizione: payload.descrizione || "",
      data_modifica: today,
    };

    return createMockResponse(GRUPPI_FORNITORI[gruppoIndex]);
  },

  DELETE: (id: number): MockResponse<ApiResponse<{ id: number }>> => {
    const gruppoIndex = GRUPPI_FORNITORI.findIndex((g) => g.id === id);

    if (gruppoIndex === -1) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${id} non trovato`,
          results: { id },
        },
      };
    }

    GRUPPI_FORNITORI.splice(gruppoIndex, 1);

    return {
      status: 200,
      data: {
        success: true,
        message: "Gruppo eliminato con successo",
        results: { id },
      },
    };
  },

  FORNITORI: (idGruppo: number): MockResponse<ApiResponse<FornitoreBase[]>> => {
    const gruppo = GRUPPI_FORNITORI.find((g) => g.id === idGruppo);

    if (!gruppo) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${idGruppo} non trovato`,
          results: [],
        },
      };
    }

    // Recupera i fornitori associati dal PersistenceManager
    const tuttiFornitori = PersistenceManager.getAllFornitori();
    const fornitori = tuttiFornitori.filter((f) =>
      gruppo.fornitori_ids.includes(f.id)
    );

    return createMockResponse(fornitori);
  },

  AGGIUNGI_FORNITORE: (
    idGruppo: number,
    idFornitore: number
  ): MockResponse<ApiResponse<GruppoFornitore>> => {
    const gruppoIndex = GRUPPI_FORNITORI.findIndex((g) => g.id === idGruppo);

    if (gruppoIndex === -1) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${idGruppo} non trovato`,
          results: {} as GruppoFornitore,
        },
      };
    }

    const fornitore = FORNITORI_BASE.find((f) => f.id === idFornitore);
    if (!fornitore) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Fornitore con ID ${idFornitore} non trovato`,
          results: {} as GruppoFornitore,
        },
      };
    }

    // Controlla se già presente
    if (GRUPPI_FORNITORI[gruppoIndex].fornitori_ids.includes(idFornitore)) {
      return {
        status: 400,
        data: {
          success: false,
          message: "Il fornitore è già presente nel gruppo",
          results: {} as GruppoFornitore,
        },
      };
    }

    // Aggiungi fornitore
    GRUPPI_FORNITORI[gruppoIndex].fornitori_ids.push(idFornitore);
    GRUPPI_FORNITORI[gruppoIndex].numero_fornitori += 1;
    GRUPPI_FORNITORI[gruppoIndex].data_modifica = new Date()
      .toISOString()
      .split("T")[0];

    return createMockResponse(GRUPPI_FORNITORI[gruppoIndex]);
  },

  RIMUOVI_FORNITORE: (
    idGruppo: number,
    idFornitore: number
  ): MockResponse<ApiResponse<GruppoFornitore>> => {
    const gruppoIndex = GRUPPI_FORNITORI.findIndex((g) => g.id === idGruppo);

    if (gruppoIndex === -1) {
      return {
        status: 404,
        data: {
          success: false,
          message: `Gruppo con ID ${idGruppo} non trovato`,
          results: {} as GruppoFornitore,
        },
      };
    }

    const fornitoreIndex = GRUPPI_FORNITORI[
      gruppoIndex
    ].fornitori_ids.indexOf(idFornitore);

    if (fornitoreIndex === -1) {
      return {
        status: 404,
        data: {
          success: false,
          message: "Il fornitore non è presente nel gruppo",
          results: {} as GruppoFornitore,
        },
      };
    }

    // Rimuovi fornitore
    GRUPPI_FORNITORI[gruppoIndex].fornitori_ids.splice(fornitoreIndex, 1);
    GRUPPI_FORNITORI[gruppoIndex].numero_fornitori -= 1;
    GRUPPI_FORNITORI[gruppoIndex].data_modifica = new Date()
      .toISOString()
      .split("T")[0];

    return createMockResponse(GRUPPI_FORNITORI[gruppoIndex]);
  },
};

/**
 * Mock responses per le API di sistema
 */
export const SISTEMA_MOCK_RESPONSES = {
  // Menu di sistema
  MENU: (): MockResponse<ApiResponse<MenuItem[]>> => {
    return createMockResponse(MENU_ITEMS);
  },

  // Dati lookup
  LOOKUP: (types?: string[]): MockResponse<ApiResponse<LookupData>> => {
    // Se vengono richiesti tipi specifici, filtriamo
    if (types && types.length > 0) {
      const filteredLookup: Partial<LookupData> = {};

      types.forEach((type) => {
        if (type in LOOKUP_DATA) {
          filteredLookup[type as keyof LookupData] =
            LOOKUP_DATA[type as keyof LookupData];
        }
      });

      return createMockResponse(filteredLookup as LookupData);
    }

    return createMockResponse(LOOKUP_DATA);
  },
};

/**
 * Factory per creare mock response dinamicamente
 */
export const createDynamicMockResponse = {
  // Crea un fornitore casuale per test
  randomFornitore: (): FornitoreDettagliato => {
    const provinces = ["RM", "MI", "NA", "TO", "FI", "BO", "PA"];
    const ragioni = [
      "Tech Solutions",
      "Digital Innovation",
      "Creative Agency",
      "Business Consulting",
    ];
    const random = Math.floor(Math.random() * 1000);

    return {
      id: 0, // Verrà assegnato dal PersistenceManager durante l'aggiunta
      operatore_economico: {
        stato: "Italia",
        possiede_partita_iva: true,
        partita_iva: `${random.toString().padStart(11, "0")}`,
        codice_fiscale_ditta: `${random.toString().padStart(11, "0")}`,
        id_soggetto: null,
      },
      azienda: {
        denominazione: `${ragioni[random % ragioni.length]} ${random} S.r.l.`,
        ragione_sociale: `${ragioni[random % ragioni.length]} ${random} S.r.l.`,
        forma_giuridica: "Società a responsabilità limitata",
        indirizzo: `Via Test ${random}`,
        comune: "Città Test",
        provincia: provinces[random % provinces.length],
        regione: "Regione Test",
        cap: "00100",
        sedi_secondarie: null,
        email_pec: `test${random}@pec.it`,
        telefono: `06-${random}`,
        fax: null,
        stato_attivita: "Attiva",
        codice_ateco: "62.01.00",
        codice_ateco_2025: "62.01.00",
        ateco_testo: "Attività di test",
        ateco_2025_testo: "Attività di test",
        anno_iscrizione_cciaa: 2020,
        numero_rea: random.toString(),
        cciaa_di: provinces[random % provinces.length],
        codice_eori: null,
        etichette: ["Test"],
      },
      referente_legale: {
        nome: "Nome Test",
        cognome: "Cognome Test",
        telefono_1: `335-${random}`,
        telefono_2: null,
        email: `test${random}@email.com`,
        qualifica: "AMMINISTRATORE UNICO",
        codice_fiscale: "TSTCDC80A01H501T",
      },
      rating_rischio: {
        cerved_group_score: Math.random() * 10,
        score_sintetico: "BB",
        score_tempi_pagamento: "Regolare",
        consistenza_immobiliare: "Media",
        situazione_economico_finanziaria: "Buona",
        profilo_strutturale: "Solido",
        rischio_connesso_num_consultazioni: "Basso",
        eventi_negativi: "Nessuno",
        cgs_valore: Math.floor(Math.random() * 100),
        cgr_classe: "2",
        cgr_descrizione: "Rischio contenuto",
        rischio_interno: "Basso",
        variazione_rischio_interno: "Stabile",
      },
      dati_economici: {
        fatturato_000_eur: Math.floor(Math.random() * 5000) + 1000,
        mol_000_eur: Math.floor(Math.random() * 500) + 100,
        margine_gestione_finanziaria_000_eur:
          Math.floor(Math.random() * 400) + 80,
        ebitda_000_eur: Math.floor(Math.random() * 500) + 100,
        utile_perdita_000_eur: Math.floor(Math.random() * 200) + 50,
        attivo_000_eur: Math.floor(Math.random() * 3000) + 500,
        passivo_000_eur: Math.floor(Math.random() * 2000) + 300,
        dipendenti: Math.floor(Math.random() * 50) + 5,
        anno_riferimento_dati_economici: 2023,
        fatturato_12m: null,
        media_fatturato: null,
        fatturato_mese: null,
        incassato: null,
        classe_di_fatturato: "1-5M",
        mese_riferimento_dati_caricati: 12,
        anno_riferimento_dati_caricati: 2023,
      },
      monitoraggio: {
        prodotto: "Standard Monitoring",
        scadenza_monitoraggio: "2024-12-31",
        utenza: "test@sistema.it",
      },
      credito_commerciale: {
        fido: Math.floor(Math.random() * 100000) + 10000,
        fido_commerciale: null,
        fido_dinamico: null,
        fido_dinamico_medio: null,
        variazione_fido_commerciale_vs_dinamico_medio: null,
        azione_da_fido_dinamico: null,
        fido_2: null,
        esposizione: null,
        scaduto: null,
        esposizione_su_fido_commerciale: null,
        dso: null,
        dso_medio: null,
        bp_dso: null,
        bp_dso_medio: null,
        add: null,
        add_medio: null,
        limite_dso: null,
        condizione_di_pagamento: "30 giorni data fattura",
        giorni_nominali: 30,
        codice_cliente_interno: `TEST${random}`,
      },
      categorie_internal_use: {
        cat1: "Test",
        cat2: null,
        cat3: null,
        cat4: null,
        cat5: null,
        cat6: null,
        cat7: null,
        cat8: null,
      },
      pagamenti: {
        iban: "IT60X0542811101000000123456",
        sdi: `TEST${random}`,
        metodo_pagamento_preferito: "Bonifico bancario",
        note: "Fornitore di test generato automaticamente",
      },
      metadati: {
        fonte: "Sistema Mock",
        versione_schema: "1.1.0",
        data_creazione: new Date().toISOString().split("T")[0],
      },
    };
  },

  // Simula errori di rete
  networkError: (
    message = "Errore di rete"
  ): MockResponse<ApiResponse<unknown>> => ({
    status: 500,
    data: {
      success: false,
      message,
      results: {},
    },
  }),

  // Simula timeout
  timeoutError: (): MockResponse<ApiResponse<unknown>> => ({
    status: 408,
    data: {
      success: false,
      message: "Timeout della richiesta",
      results: {},
    },
  }),
};

/**
 * Mock per gestione gruppi di un singolo fornitore
 */
export const FORNITORI_GRUPPI_MOCK_RESPONSES = {
  /**
   * GET /v1/fornitori/{id}/gruppi - Ottiene i gruppi di un fornitore
   */
  getGruppiFornitore: (fornitoreId: number): MockResponse<ApiResponse<GruppoFornitore[]>> => {
    // Trova i gruppi che contengono questo fornitore
    const gruppi = GRUPPI_FORNITORI.filter(gruppo =>
      gruppo.fornitori_ids.includes(fornitoreId)
    );

    return {
      status: 200,
      data: {
        success: true,
        results: gruppi,
        message: `Trovati ${gruppi.length} gruppi per il fornitore ${fornitoreId}`,
      },
    };
  },

  /**
   * PUT /v1/fornitori/{id}/gruppi - Aggiorna i gruppi di un fornitore
   * @param fornitoreId ID del fornitore
   * @param gruppiIds Array di ID dei gruppi a cui associare il fornitore
   */
  updateGruppiFornitore: (
    fornitoreId: number,
    gruppiIds: number[]
  ): MockResponse<ApiResponse<{ fornitoreId: number; gruppiIds: number[] }>> => {
    // Rimuovi il fornitore da tutti i gruppi
    GRUPPI_FORNITORI.forEach(gruppo => {
      const index = gruppo.fornitori_ids.indexOf(fornitoreId);
      if (index > -1) {
        gruppo.fornitori_ids.splice(index, 1);
        gruppo.numero_fornitori = gruppo.fornitori_ids.length;
      }
    });

    // Aggiungi il fornitore ai nuovi gruppi
    gruppiIds.forEach(gruppoId => {
      const gruppo = GRUPPI_FORNITORI.find(g => g.id === gruppoId);
      if (gruppo && !gruppo.fornitori_ids.includes(fornitoreId)) {
        gruppo.fornitori_ids.push(fornitoreId);
        gruppo.numero_fornitori = gruppo.fornitori_ids.length;
        gruppo.data_modifica = new Date().toISOString().split('T')[0];
      }
    });

    return {
      status: 200,
      data: {
        success: true,
        results: {
          fornitoreId,
          gruppiIds,
        },
        message: `Gruppi del fornitore ${fornitoreId} aggiornati con successo`,
      },
    };
  },
};

/**
 * Esporta tutti i mock per l'utilizzo nel sistema
 */
export const API_MOCKS = {
  FORNITORI: FORNITORI_MOCK_RESPONSES,
  FORNITORI_GRUPPI: FORNITORI_GRUPPI_MOCK_RESPONSES,
  GRUPPI_FORNITORI: GRUPPI_FORNITORI_MOCK_RESPONSES,
  SISTEMA: SISTEMA_MOCK_RESPONSES,
  DYNAMIC: createDynamicMockResponse,
};
